import { ExpiringCertification } from '@common/types/certification.types'
import Icon from '@components/dataDisplay/Icon'
import Chip from '@components/layout/Chip'
import { GREY_COLORS, PARKER_GOLDEN_COLORS, RED_COLORS } from '@config/theme/colors'
import { FontWeight } from '@config/theme/sizes'
import { DeleteOutlineRounded } from '@mui/icons-material'
import { Stack, styled, Typography } from '@mui/material'
import { useCertificationApi } from '@services/api/certification.api'
import { useSnackbar } from '@services/contexts/SnackbarContext'
import { useMutation } from '@tanstack/react-query'
import { isBefore } from 'date-fns'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'

type ExpiringCertificationCardProps = {
	certification: ExpiringCertification
	onDiscardClick: () => void
}

/**
 * Expiring certification card
 * @param certification
 * @param onDiscardClick
 * @constructor
 */
const ExpiringCertificationCard = (
	{
		certification,
		onDiscardClick
	}: ExpiringCertificationCardProps
) => {
	const { updateExpiryNotification } = useCertificationApi()
	const { formatMessage } = useIntl()
	const { addErrorSnackbar, addSnackbar } = useSnackbar()

	const isBeforeNow = isBefore(new Date(certification.expiryDate), new Date())

	const { mutate: updateExpiryNotificationMutate } = useMutation<ExpiringCertification, any, { id: string, value: boolean }>({
		mutationKey: ['updateExpiryNotification'],
		mutationFn: ({ id, value }) => updateExpiryNotification(id, value),
		onSuccess: () => {
			addSnackbar(formatMessage({ id: 'dashboard.expiringCertifications.list.discardButton.success' }))
			onDiscardClick()
		},
		onError: () => addErrorSnackbar()
	})

	return (
		<Container to={certification.traineeId}>
			<CertificationTrainee>
				<FormattedMessage
					id="dashboard.expiringCertifications.list.trainee"
					values={{
						a: (text) => <CertificationTraineeText>{text}</CertificationTraineeText>,
						b: (text) => <Typography key="trainee" fontWeight={FontWeight.MEDIUM}>{text}</Typography>,
						c: (text) => <Typography key="company" variant="body2">{text}</Typography>,
						name: `${certification.lastname} ${certification.firstname}`,
						company: certification.companyName
					}}
				/>
				<Chip color={PARKER_GOLDEN_COLORS}>
					<FormattedMessage id={`global.enums.companyType.${certification.companyType}`} />
				</Chip>
			</CertificationTrainee>
			<CertificationCourse>
				<Typography color={GREY_COLORS[500]} variant="body2">
					<FormattedMessage id={`global.enums.certificationCourse.${certification.course}`} />
				</Typography>
				{certification.trainerIndicator && (
					<Chip color={GREY_COLORS}>
						<FormattedMessage id="dashboard.expiringCertifications.list.trainer" />
					</Chip>
				)}
			</CertificationCourse>
			<CertificationExpiration>
				<Typography color={isBeforeNow ? RED_COLORS[300] : GREY_COLORS[500]} variant="body2">
					<FormattedMessage
						id="dashboard.expiringCertifications.list.date"
						values={{ date: new Intl.DateTimeFormat().format(new Date(certification.expiryDate)) }}
					/>
				</Typography>
				<DiscardButton
					className="discardButton"
					onClick={(event) => {
						event.preventDefault()
						updateExpiryNotificationMutate({ id: certification.id, value: false })
					}}
				>
					<Typography variant="subtitle2">
						<FormattedMessage id="dashboard.expiringCertifications.list.discardButton.label" />
					</Typography>
					<Icon IconElement={DeleteOutlineRounded} size="16px" />
				</DiscardButton>
			</CertificationExpiration>
		</Container>
	)
}

const Container = styled(Link)({
	color: 'inherit',
	padding: '8px 12px',
	rowGap: '2px',
	textDecoration: 'none',
	'&:hover': {
		backgroundColor: GREY_COLORS[50],
		'& .discardButton': {
			opacity: 1
		}
	}
})

const CertificationTrainee = styled(Stack)({
	alignItems: 'center',
	columnGap: '8px',
	flexDirection: 'row',
	height: '24px'
})

const CertificationTraineeText = styled(Stack)({
	alignItems: 'center',
	columnGap: '8px',
	flexDirection: 'row'
})

const CertificationCourse = styled(Stack)({
	alignItems: 'center',
	columnGap: '8px',
	flexDirection: 'row',
	height: '24px'
})

const CertificationExpiration = styled(Stack)({
	alignItems: 'center',
	flexDirection: 'row',
	justifyContent: 'space-between'
})

const DiscardButton = styled(Stack)({
	alignItems: 'center',
	backgroundColor: 'transparent',
	border: `1px solid ${GREY_COLORS[200]}`,
	borderRadius: '8px',
	columnGap: '4px',
	cursor: 'pointer',
	flexDirection: 'row',
	opacity: 0,
	padding: '4px'
})

export default ExpiringCertificationCard
